<template>
  <div class="admin-panel task-page task-page--email-blast">

    <admin-header
      variant="back"
      :crumbs="[{ label: 'Client Management', to: { name: 'Admin-Panel', params: { lang: $route.params.lang } } }, { label: 'Email Blast', to: { name: 'Admin-Email-Blast', params: { lang: $route.params.lang } } }, { label: 'General Blast' }]"
      :back-to="{ name: 'Admin-Email-Blast', params: { lang: $route.params.lang } }"
      @open-logout="openLogoutModal"
    />

    <main class="ap-container ap-fade-in">

      <!-- Hero -->
      <div class="tp-hero">
        <div class="tp-hero-icon">
          <fa-icon :icon="['fas', 'envelope-open-text']" />
        </div>
        <div>
          <h1 class="tp-hero-title">General Email Blast</h1>
          <p class="tp-hero-sub">Compose a custom email and send it to any filtered group of clients. Filter columns to narrow your recipients, then blast.</p>
        </div>
      </div>

      <!-- ── Email Composer ─────────────────────────────────────── -->
      <div class="tp-panel geb-composer-panel">
        <div class="tp-panel-head">
          <h3><fa-icon :icon="['fas', 'pen']" style="margin-right:6px" />Email Composer</h3>
        </div>
        <div class="geb-composer-body">
          <div class="geb-field">
            <label class="geb-label">Subject</label>
            <input
              ref="subjectInput"
              v-model="composer.subject"
              type="text"
              class="geb-input"
              placeholder="Enter email subject…"
              maxlength="200"
            />
          </div>
          <div class="geb-field">
            <label class="geb-label">Message</label>
            <div class="geb-toolbar">
              <button class="geb-tb-btn" title="Bold"      @mousedown.prevent="execCmd('bold')"><strong>B</strong></button>
              <button class="geb-tb-btn" title="Italic"    @mousedown.prevent="execCmd('italic')"><em>I</em></button>
              <button class="geb-tb-btn" title="Underline" @mousedown.prevent="execCmd('underline')"><u>U</u></button>
              <span class="geb-tb-sep"></span>
              <button class="geb-tb-btn" title="Bullet list"   @mousedown.prevent="execCmd('insertUnorderedList')"><fa-icon :icon="['fas', 'list-ul']" /></button>
              <button class="geb-tb-btn" title="Numbered list"  @mousedown.prevent="execCmd('insertOrderedList')"><fa-icon :icon="['fas', 'list-ol']" /></button>
              <span class="geb-tb-sep"></span>
              <button class="geb-tb-btn" title="Clear format" @mousedown.prevent="execCmd('removeFormat')"><fa-icon :icon="['fas', 'remove-format']" /></button>
            </div>
            <div
              ref="editor"
              class="geb-editor"
              contenteditable="true"
              data-placeholder="Type your message here…"
              @input="onEditorInput"
            ></div>
          </div>
          <div class="geb-field">
            <label class="geb-label">
              Attachment
              <span class="geb-label-opt">(optional · PDF, Word, Excel, Image · max 5 MB)</span>
            </label>
            <div v-if="attachment.uploading" class="geb-attach-state">
              <fa-icon :icon="['fas', 'spinner']" spin /> Uploading…
            </div>
            <div v-else-if="attachment.token" class="geb-attach-preview">
              <fa-icon :icon="['fas', 'file']" class="geb-attach-icon" />
              <span class="geb-attach-name">{{ attachment.name }}</span>
              <span class="geb-attach-size">{{ formatFileSize(attachment.size) }}</span>
              <button class="geb-attach-remove" title="Remove attachment" @click="removeAttachment">
                <fa-icon :icon="['fas', 'times']" />
              </button>
            </div>
            <label v-else class="geb-attach-btn">
              <fa-icon :icon="['fas', 'cloud-upload-alt']" />
              Attach a file
              <input
                ref="attachInput"
                type="file"
                class="geb-attach-input"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.csv"
                @change="onAttachmentChange"
              />
            </label>
            <p v-if="attachError" class="geb-attach-error">
              <fa-icon :icon="['fas', 'exclamation-circle']" /> {{ attachError }}
            </p>
          </div>

          <p class="geb-composer-note">
            <fa-icon :icon="['fas', 'info-circle']" />
            The greeting <em>"Dear [Recipient Name],"</em> and your signature are added automatically.
          </p>
        </div>
      </div>

      <!-- ── Summary Strip ─────────────────────────────────────── -->
      <div v-if="!loading && tableRows.length" class="tp-summary">
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ tableRows.length }}</span>
          <span class="tp-summary-label">Total Clients</span>
        </div>
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ filteredRows.length }}</span>
          <span class="tp-summary-label">Filtered</span>
        </div>
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ eligibleCount }}</span>
          <span class="tp-summary-label">Eligible to Send</span>
        </div>
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ noRecipientCount }}</span>
          <span class="tp-summary-label">No Recipient</span>
        </div>
      </div>

      <!-- ── Table Panel ────────────────────────────────────────── -->
      <div class="tp-panel">
        <!-- Panel head: title (left) | badge + blast button (right) -->
        <div class="tp-panel-head">
          <h3>Client Recipients</h3>
          <div class="tp-panel-head-right">
            <app-badge v-if="!loading" size="xl" :caps="false" variant="custom" custom-color="#333739" custom-bg="#F2F3F3" :inner-shadow="true">
              <span>{{ filteredRows.length }} / {{ tableRows.length }}</span>
            </app-badge>
            <app-button
              size="sm"
              variant="custom"
              custom-bg="#2563eb"
              custom-hover-bg="#1E40AF"
              :icon="['fas', blasting.done ? 'check' : 'paper-plane']"
              :disabled="loading || blasting.active || blasting.done || !eligibleCount"
              @click="runBlast"
            >{{ blasting.done ? 'Blast Complete' : `Blast ${eligibleCount}` }}</app-button>
          </div>
        </div>
        <!-- Toolbar: Row 1 = status filters | Row 2 = group toggle chips -->
        <div class="ap-toolbar email-general">
          <div class="ap-toolbar-row">
            <button
              v-for="s in clientStatusOptions"
              :key="s.value"
              class="ap-filter-chip"
              :class="{ active: clientStatusFilter === s.value }"
              @click="clientStatusFilter = s.value"
            >{{ s.label }}</button>
          </div>
          <div class="ap-toolbar-row">
            <button
              v-for="g in colGroupDefs"
              :key="g.key"
              class="ap-filter-chip ap-filter-chip-cat"
              :class="['ap-filter-chip-cat--' + g.key, { active: !collapsedGroups.includes(g.key) }]"
              :title="(collapsedGroups.includes(g.key) ? 'Show' : 'Hide') + ' ' + g.label"
              @click="toggleGroup(g.key)"
            >{{ g.label }}</button>
          </div>
        </div>

        <div v-if="hasActiveFilters" class="geb-filter-reset-bar">
          <fa-icon :icon="['fas', 'filter']" />
          Filters active — showing {{ filteredRows.length }} of {{ tableRows.length }} clients.
          <button class="geb-clear-btn" @click="clearAllFilters">
            <fa-icon :icon="['fas', 'times']" /> Clear all filters
          </button>
        </div>

        <div v-if="loading" class="tp-empty">
          <fa-icon :icon="['fas', 'spinner']" spin class="tp-empty-icon" />
          <p>Loading clients…</p>
        </div>
        <div v-else-if="!tableRows.length" class="tp-empty">
          <fa-icon :icon="['fas', 'users']" class="tp-empty-icon" />
          <p>No clients found.</p>
        </div>

        <div v-else class="tp-notify-table-wrap geb-table-wrap">
          <table class="tp-notify-table geb-table">
            <thead>
              <!-- Row 1: Group headers + inline filters for stable columns -->
              <tr class="geb-thead-groups">
                <th rowspan="2" class="geb-th-num">#</th>
                <th rowspan="2" class="geb-th-client">
                  <div class="geb-col-label">Client</div>
                  <input v-model="f.clientName" class="geb-col-filter" placeholder="Filter client…" />
                </th>
                <th rowspan="2" class="geb-th-initial">
                  <div class="geb-col-label">Initial</div>
                  <input v-model="f.initial" class="geb-col-filter" placeholder="Filter…" />
                </th>
                <th colspan="5" class="geb-th-group">Email Group</th>
                <th rowspan="2" class="geb-th-basic">
                  <div class="geb-col-label">Business Type</div>
                  <select v-model="f.businessType" class="geb-col-filter geb-col-select">
                    <option value="">All Types</option>
                    <option v-for="t in businessTypeOptions" :key="t.value" :value="t.value">{{ t.label }}</option>
                  </select>
                </th>
                <th rowspan="2" class="geb-th-basic">
                  <div class="geb-col-label">Service Type</div>
                  <div class="geb-svc-filter" ref="svcDropdown">
                    <button class="geb-col-filter geb-svc-btn" @click.stop="showSvcMenu = !showSvcMenu">
                      {{ f.serviceTypes.length ? f.serviceTypes.length + ' selected' : 'All Services' }}
                      <fa-icon :icon="['fas', 'chevron-down']" class="geb-svc-caret" />
                    </button>
                    <div v-if="showSvcMenu" class="geb-svc-menu">
                      <label v-for="s in serviceTypeOptions" :key="s.value" class="geb-svc-item">
                        <input type="checkbox" :value="s.value" v-model="f.serviceTypes" />
                        {{ s.label }}
                      </label>
                      <div class="geb-svc-footer">
                        <button @click="f.serviceTypes = []">Clear</button>
                      </div>
                    </div>
                  </div>
                </th>
                <th rowspan="2" class="geb-th-basic">
                  <div class="geb-col-label">Year End</div>
                  <select v-model="f.yearEnd" class="geb-col-filter geb-col-select">
                    <option value="">All</option>
                    <option v-for="(m, i) in months" :key="i + 1" :value="i + 1">{{ m }}</option>
                  </select>
                </th>
                <template v-for="g in visibleColGroups">
                  <th :key="g.key" :colspan="g.cols.length" class="geb-th-group">{{ g.label }}</th>
                </template>
                <th rowspan="2" class="geb-th-status">Status</th>
              </tr>

              <!-- Row 2: Email sub-column headers/checkboxes + category column headers/filters -->
              <tr class="geb-thead-filters">
                <th class="geb-th-email-sub" :class="{ 'geb-col-disabled': !emailGroupFilters.pic }">
                  <div class="geb-col-label">PIC</div>
                  <label class="geb-col-check">
                    <input type="checkbox" v-model="emailGroupFilters.pic" />
                    Include
                  </label>
                </th>
                <th class="geb-th-email-sub" :class="{ 'geb-col-disabled': !emailGroupFilters.to }">
                  <div class="geb-col-label">TO</div>
                  <label class="geb-col-check">
                    <input type="checkbox" v-model="emailGroupFilters.to" />
                    Include
                  </label>
                </th>
                <th class="geb-th-email-sub" :class="{ 'geb-col-disabled': !emailGroupFilters.cc }">
                  <div class="geb-col-label">CC</div>
                  <label class="geb-col-check">
                    <input type="checkbox" v-model="emailGroupFilters.cc" />
                    Include
                  </label>
                </th>
                <th class="geb-th-email-sub" :class="{ 'geb-col-disabled': !emailGroupFilters.bcc }">
                  <div class="geb-col-label">BCC</div>
                  <label class="geb-col-check">
                    <input type="checkbox" v-model="emailGroupFilters.bcc" />
                    Include
                  </label>
                </th>
                <th class="geb-th-email-sub" :class="{ 'geb-col-disabled': !emailGroupFilters.director }">
                  <div class="geb-col-label">Director</div>
                  <label class="geb-col-check">
                    <input type="checkbox" v-model="emailGroupFilters.director" />
                    Include
                  </label>
                </th>
                <template v-for="g in visibleColGroups">
                  <th
                    v-for="(col, ci) in g.cols"
                    :key="col.key"
                    :class="{ 'geb-cat-group-start': ci === 0 }"
                  >
                    <div class="geb-col-label">{{ col.label }}</div>
                    <select v-model="catFilters[col.key]" class="geb-col-filter geb-col-select">
                      <option value="">All</option>
                      <option v-if="col.type === 'date'"   value="has">Has Date</option>
                      <option v-if="col.type === 'date'"   value="empty">No Date</option>
                      <option v-if="col.type === 'status'" value="pending">Pending</option>
                      <option v-if="col.type === 'status'" value="review">Review</option>
                      <option v-if="col.type === 'status'" value="completed">Completed</option>
                    </select>
                  </th>
                </template>
              </tr>
            </thead>

            <tbody>
              <tr v-if="!filteredRows.length">
                <td :colspan="totalColCount" class="ap-empty">No clients match the current filters.</td>
              </tr>
              <tr v-for="(row, i) in filteredRows" :key="row.id" :class="'geb-row--' + row.status">
                <td class="geb-td-num">{{ i + 1 }}</td>
                <td class="geb-td-client">{{ row.client }}</td>
                <td class="geb-td-meta">{{ row.initial || '—' }}</td>

                <!-- PIC (always TO, no checkbox) -->
                <td class="geb-td-emails">
                  <div v-for="r in row.picRecipients" :key="'pic-' + r.email" class="geb-email-chip geb-email-chip--fixed">
                    <fa-icon :icon="['fas', 'user-tie']" />
                    <span>{{ r.email }}</span>
                  </div>
                  <span v-if="!row.picRecipients.length" class="tp-notify-no-recipient">—</span>
                </td>

                <!-- TO with checkboxes -->
                <td class="geb-td-emails">
                  <label
                    v-for="r in row.toRecipients"
                    :key="'to-' + r.email"
                    class="geb-email-check"
                    :class="{ 'geb-email-check--off': !r.enabled }"
                  >
                    <input type="checkbox" v-model="r.enabled" />
                    <span>{{ r.email }}</span>
                  </label>
                  <span v-if="!row.toRecipients.length" class="tp-notify-no-recipient">—</span>
                </td>

                <!-- CC with checkboxes -->
                <td class="geb-td-emails">
                  <label
                    v-for="r in row.ccRecipients"
                    :key="'cc-' + r.email"
                    class="geb-email-check"
                    :class="{ 'geb-email-check--off': !r.enabled }"
                  >
                    <input type="checkbox" v-model="r.enabled" />
                    <span>{{ r.email }}</span>
                  </label>
                  <span v-if="!row.ccRecipients.length" class="tp-notify-no-recipient">—</span>
                </td>

                <!-- BCC with checkboxes -->
                <td class="geb-td-emails">
                  <label
                    v-for="r in row.bccRecipients"
                    :key="'bcc-' + r.email"
                    class="geb-email-check"
                    :class="{ 'geb-email-check--off': !r.enabled }"
                  >
                    <input type="checkbox" v-model="r.enabled" />
                    <span>{{ r.email }}</span>
                  </label>
                  <span v-if="!row.bccRecipients.length" class="tp-notify-no-recipient">—</span>
                </td>

                <!-- Director (always TO, no checkbox) -->
                <td class="geb-td-emails">
                  <div v-for="r in row.directorRecipients" :key="'dir-' + r.email" class="geb-email-chip geb-email-chip--fixed">
                    <fa-icon :icon="['fas', 'user-shield']" />
                    <span>{{ r.email }}</span>
                  </div>
                  <span v-if="!row.directorRecipients.length" class="tp-notify-no-recipient">—</span>
                </td>

                <td class="geb-td-meta">{{ formatClientType(row.client_type) }}</td>

                <td class="geb-td-services">
                  <div class="tp-svc-tags">
                    <span
                      v-for="cat in row.category"
                      :key="cat.name"
                      class="ap-svc-badge"
                      :class="'ap-svc-' + cat.name"
                    >{{ cat.display_name || cat.name }}</span>
                  </div>
                  <span v-if="!row.category.length" class="tp-notify-no-recipient">—</span>
                </td>

                <td class="geb-td-meta">{{ formatYearEnd(row.year_end) }}</td>

                <!-- Category columns -->
                <template v-for="g in visibleColGroups">
                  <td
                    v-for="(col, ci) in g.cols"
                    :key="col.key"
                    class="geb-td-cat"
                    :class="{ 'geb-cat-group-start': ci === 0 }"
                  >
                    <span
                      class="ap-ci-status"
                      :class="'ap-ci-status-' + (getCatColValue(row, col) || 'na')"
                    >{{ formatCatColValue(getCatColValue(row, col), col.type) }}</span>
                  </td>
                </template>

                <!-- Blast status -->
                <td class="eb-td-status">
                  <span class="tp-notify-badge" :class="blastBadgeClass(row)">
                    <fa-icon v-if="row.status === 'sending'" :icon="['fas', 'spinner']" spin />
                    <fa-icon v-else-if="row.status === 'sent'"    :icon="['fas', 'check']" />
                    <fa-icon v-else-if="row.status === 'failed'"  :icon="['fas', 'times']" />
                    <fa-icon v-else-if="row.status === 'no-recipient'" :icon="['fas', 'minus']" />
                    {{ blastStatusLabel(row) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </main>

    <!-- Blast progress overlay -->
    <div v-if="blasting.active" class="eb-blast-overlay">
      <div class="eb-blast-card">
        <fa-icon :icon="['fas', 'paper-plane']" class="eb-blast-plane" spin />
        <div class="eb-blast-card-title">Email Blast in Progress</div>
        <div class="eb-blast-card-sub">Please do not close or navigate away from this tab.</div>
        <div class="eb-blast-counts">
          <div class="eb-blast-count eb-blast-count--sent">
            <span class="eb-blast-count-val">{{ sentCount }}</span>
            <span class="eb-blast-count-label">Sent</span>
          </div>
          <div class="eb-blast-count eb-blast-count--skip">
            <span class="eb-blast-count-val">{{ skippedCount }}</span>
            <span class="eb-blast-count-label">Skipped</span>
          </div>
          <div class="eb-blast-count eb-blast-count--fail">
            <span class="eb-blast-count-val">{{ failedCount }}</span>
            <span class="eb-blast-count-label">Failed</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary modal -->
    <div v-if="summary.show" class="tp-notify-overlay" @click.self="resetAndCloseSummary">
      <div class="tp-notify-modal eb-summary-modal">
        <div class="tp-notify-head">
          <div class="tp-notify-head-title">
            <fa-icon :icon="['fas', 'paper-plane']" />
            General Email Blast — Summary
          </div>
          <div class="tp-notify-head-sub">Blast complete</div>
          <button class="tp-notify-close" @click="resetAndCloseSummary">
            <fa-icon :icon="['fas', 'times']" />
          </button>
        </div>
        <div class="tp-notify-body eb-summary-body">
          <div class="eb-summary-grid" :class="{ 'eb-summary-grid--4': summary.partial > 0 }">
            <div class="eb-summary-stat eb-summary-stat--sent">
              <span class="eb-summary-val">{{ summary.sent }}</span>
              <span class="eb-summary-label">Sent</span>
            </div>
            <div v-if="summary.partial > 0" class="eb-summary-stat eb-summary-stat--partial">
              <span class="eb-summary-val">{{ summary.partial }}</span>
              <span class="eb-summary-label">Partial</span>
            </div>
            <div class="eb-summary-stat eb-summary-stat--skip">
              <span class="eb-summary-val">{{ summary.skipped }}</span>
              <span class="eb-summary-label">Skipped</span>
            </div>
            <div class="eb-summary-stat eb-summary-stat--fail">
              <span class="eb-summary-val">{{ summary.failed }}</span>
              <span class="eb-summary-label">Failed</span>
            </div>
          </div>
          <div v-if="summary.errors.length" class="eb-summary-errors">
            <div class="eb-summary-errors-title">Failed Sends</div>
            <div v-for="e in summary.errors" :key="e.client" class="eb-summary-error-row">
              <span class="eb-summary-error-client">{{ e.client }}</span>
              <span class="eb-summary-error-msg">{{ e.error }}</span>
            </div>
          </div>
        </div>
        <div class="tp-notify-foot">
          <button class="tp-notify-send-btn tp-notify-send-btn--done" @click="resetAndCloseSummary">
            <fa-icon :icon="['fas', 'check']" /> Done
          </button>
        </div>
      </div>
    </div>

    <confirm-modal
      :show="showLogoutModal"
      title="Confirm Logout"
      confirm-label="Logout"
      :icon="['fas', 'sign-out-alt']"
      @cancel="closeLogoutModal"
      @confirm="confirmLogout"
    >Are you sure you want to logout?</confirm-modal>

    <session-modal :show="showAdminSessionModal" @continue="continueAdminSession" @signout="signOutAdminSession">
      Your admin session has expired. Continue to refresh your session or sign out.
    </session-modal>

  </div>
</template>

<script>
import adminSessionMixin from '@/mixins/adminSessionMixin'
import AdminHeader from '@/components/admin/AdminHeader.vue'
import ConfirmModal from '@/components/shared/ConfirmModal.vue'
import SessionModal from '@/components/shared/SessionModal.vue'
import AppButton from '@/components/shared/AppButton.vue'
import AppBadge from '@/components/shared/AppBadge.vue'
import { lockBodyScroll, unlockBodyScroll } from '@/utils/bodyScrollLock'

const GB_COL_DEFS = [
  // Tax
  { key: 'ci__tax__tax_filing', label: 'Tax Filing', cat: 'tax', path: ['tax_filing'], type: 'date', group: 'tax' },
  { key: 'ci__tax__mitrs', label: 'MITRS', cat: 'tax', path: ['mitrs'], type: 'date', group: 'tax' },
  { key: 'ci__tax__cp204', label: 'CP204', cat: 'tax', path: ['cp204'], type: 'date', group: 'tax' },
  { key: 'ci__tax__cp204a6', label: 'CP204A(6)', cat: 'tax', path: ['cp204a6'], type: 'date', group: 'tax' },
  { key: 'ci__tax__cp204a9', label: 'CP204A(9)', cat: 'tax', path: ['cp204a9'], type: 'date', group: 'tax' },
  { key: 'ci__tax__cp204a11', label: 'CP204A(11)', cat: 'tax', path: ['cp204a11'], type: 'date', group: 'tax' },
  // TP Reminder
  { key: 'ci__tp__tp_reminder', label: 'TP Reminder', cat: 'tp_reminder', path: ['tp_reminder'], type: 'date', group: 'tp_reminder' },
  // Cosec
  { key: 'ci__cosec__ar', label: 'AR', cat: 'cosec', path: ['compliance', 'ar'], type: 'date', group: 'cosec' },
  { key: 'ci__cosec__fs', label: 'FS', cat: 'cosec', path: ['compliance', 'fs'], type: 'date', group: 'cosec' },
  { key: 'ci__cosec__ar_rem1', label: 'AR Reminder 1', cat: 'cosec', path: ['reminder', 'ar_reminder_1st'], type: 'date', group: 'cosec' },
  { key: 'ci__cosec__ar_rem2', label: 'AR Reminder 2', cat: 'cosec', path: ['reminder', 'ar_reminder_2nd'], type: 'date', group: 'cosec' },
  { key: 'ci__cosec__ar_late1', label: 'AR Late 1', cat: 'cosec', path: ['reminder', 'ar_reminder_late_1'], type: 'date', group: 'cosec' },
  { key: 'ci__cosec__ar_late2', label: 'AR Late 2', cat: 'cosec', path: ['reminder', 'ar_reminder_late_2'], type: 'date', group: 'cosec' },
  { key: 'ci__cosec__afs_rem1', label: 'AFS Reminder 1', cat: 'cosec', path: ['reminder', 'afs_reminder_1st'], type: 'date', group: 'cosec' },
  { key: 'ci__cosec__afs_rem2', label: 'AFS Reminder 2', cat: 'cosec', path: ['reminder', 'afs_reminder_2nd'], type: 'date', group: 'cosec' },
  { key: 'ci__cosec__afs_late1', label: 'AFS Late 1', cat: 'cosec', path: ['reminder', 'afs_reminder_late_1'], type: 'date', group: 'cosec' },
  { key: 'ci__cosec__afs_late2', label: 'AFS Late 2', cat: 'cosec', path: ['reminder', 'afs_reminder_late_2'], type: 'date', group: 'cosec' },
  // Incorporation
  { key: 'ci__inc__s58_236', label: 'S58 & 236', cat: 'Incorporation', path: ['submit_S58_236'], type: 'date', group: 'incorporation' },
  { key: 'ci__inc__s472_473', label: 'S47(2)/S47(3)', cat: 'Incorporation', path: ['submit_S472_473'], type: 'date', group: 'incorporation' },
  { key: 'ci__inc__benef', label: 'Benef. Ownership', cat: 'Incorporation', path: ['submit_beneficial_ownership'], type: 'date', group: 'incorporation' },
  { key: 'ci__inc__m1', label: 'M1 Stamp', cat: 'Incorporation', path: ['pay_form_m1_stamp_duty'], type: 'date', group: 'incorporation' },
  { key: 'ci__inc__m3', label: 'M3 Stamp', cat: 'Incorporation', path: ['pay_form_m3_stamp_duty'], type: 'date', group: 'incorporation' },
  { key: 'ci__inc__co_stamp', label: 'Co. Stamp', cat: 'Incorporation', path: ['order_company_stamp'], type: 'status', group: 'incorporation' },
  { key: 'ci__inc__sup_file', label: 'Superior File', cat: 'Incorporation', path: ['create_superior_file'], type: 'status', group: 'incorporation' },
  { key: 'ci__inc__phy_file', label: 'Physical File', cat: 'Incorporation', path: ['create_physical_file'], type: 'status', group: 'incorporation' },
  { key: 'ci__inc__cp204_1st', label: 'CP204 (1st)', cat: 'Incorporation', path: ['submit_form_1st_cp204'], type: 'status', group: 'incorporation' },
  // Payroll Yearly
  { key: 'ci__pay_yr__ea', label: 'Form EA', cat: 'payroll_yearly', path: ['form_ea'], type: 'date', group: 'payroll_yearly' },
  { key: 'ci__pay_yr__e', label: 'Form E', cat: 'payroll_yearly', path: ['form_e'], type: 'date', group: 'payroll_yearly' },
  // Payroll Reminder
  { key: 'ci__pay_rm__rem', label: 'PR Reminder', cat: 'payroll_reminder', path: ['payroll_reminder'], type: 'date', group: 'payroll_reminder' },
  // Payroll Monthly
  { key: 'ci__pay_mo__payslip', label: 'Payslip', cat: 'payroll_monthly', path: ['payslip'], type: 'date', group: 'payroll_monthly' },
  { key: 'ci__pay_mo__pcb', label: 'PCB/EPF/…', cat: 'payroll_monthly', path: ['pcb_epf_socso_eis_hrdc'], type: 'date', group: 'payroll_monthly' },
  { key: 'ci__pay_mo__payday', label: 'Payday', cat: 'payroll_monthly', path: ['payday'], type: 'date', group: 'payroll_monthly' },
  // Accounting
  { key: 'ci__acct__sst1', label: 'SST 1', cat: 'accounting', path: ['sst1'], type: 'date', group: 'accounting' },
  { key: 'ci__acct__sst2', label: 'SST 2', cat: 'accounting', path: ['sst2'], type: 'date', group: 'accounting' },
  { key: 'ci__acct__sst3', label: 'SST 3', cat: 'accounting', path: ['sst3'], type: 'date', group: 'accounting' },
  { key: 'ci__acct__sst4', label: 'SST 4', cat: 'accounting', path: ['sst4'], type: 'date', group: 'accounting' },
  { key: 'ci__acct__sst5', label: 'SST 5', cat: 'accounting', path: ['sst5'], type: 'date', group: 'accounting' },
  { key: 'ci__acct__sst6', label: 'SST 6', cat: 'accounting', path: ['sst6'], type: 'date', group: 'accounting' }
]

const GROUP_META = [
  { key: 'tax', label: 'Tax' },
  { key: 'tp_reminder', label: 'TP Reminder' },
  { key: 'cosec', label: 'Cosec' },
  { key: 'incorporation', label: 'Incorporation' },
  { key: 'payroll_yearly', label: 'Payroll Yearly' },
  { key: 'payroll_reminder', label: 'Payroll Reminder' },
  { key: 'payroll_monthly', label: 'Payroll Monthly' },
  { key: 'accounting', label: 'Accounting' }
]

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export default {
  name: 'admin-email-blast-general',
  mixins: [adminSessionMixin],
  components: { AdminHeader, ConfirmModal, SessionModal, AppButton, AppBadge },

  data () {
    const catFilters = {}
    GB_COL_DEFS.forEach(d => { catFilters[d.key] = '' })

    return {
      loading: false,
      tableRows: [],

      clientStatusFilter: 'active',
      clientStatusOptions: [
        { value: '', label: 'All' },
        { value: 'active', label: 'Active' },
        { value: 'terminated', label: 'Terminated' }
      ],

      composer: { subject: '', messageHtml: '' },

      f: {
        clientName: '',
        initial: '',
        businessType: '',
        serviceTypes: [],
        yearEnd: ''
      },
      catFilters,

      attachment: { token: null, name: '', size: 0, uploading: false },
      attachError: '',

      emailGroupFilters: { pic: false, to: true, cc: true, bcc: true, director: false },

      collapsedGroups: [],
      showSvcMenu: false,

      blasting: { active: false, done: false },
      summary: {
        show: false,
        sent: 0,
        partial: 0,
        skipped: 0,
        failed: 0,
        errors: []
      },

      months: MONTHS,
      businessTypeOptions: [
        { value: 'sdn_bhd', label: 'Sdn Bhd' },
        { value: 'llp', label: 'LLP' },
        { value: 'sole_prop', label: 'Sole Prop' },
        { value: 'partnership', label: 'Partnership' },
        { value: 'salaried_person', label: 'Salaried' }
      ],
      serviceTypeOptions: [
        { value: 'tax', label: 'Tax' },
        { value: 'tp_reminder', label: 'TP Reminder' },
        { value: 'cosec', label: 'Cosec' },
        { value: 'Incorporation', label: 'Incorporation' },
        { value: 'payroll_monthly', label: 'Payroll Monthly' },
        { value: 'payroll_yearly', label: 'Payroll Yearly' },
        { value: 'payroll_reminder', label: 'Payroll Reminder' },
        { value: 'accounting', label: 'Accounting' }
      ]
    }
  },

  computed: {
    colGroupDefs () { return GROUP_META },

    visibleColGroups () {
      return GROUP_META
        .filter(g => !this.collapsedGroups.includes(g.key))
        .map(g => ({ ...g, cols: GB_COL_DEFS.filter(c => c.group === g.key) }))
    },

    totalColCount () {
      return 13 + this.visibleColGroups.reduce((s, g) => s + g.cols.length, 0)
    },

    filteredRows () {
      const { clientName, initial, businessType, serviceTypes, yearEnd } = this.f
      const cf = this.catFilters
      return this.tableRows.filter(row => {
        if (this.clientStatusFilter && (row.clientStatus || 'active').toLowerCase() !== this.clientStatusFilter) return false
        if (clientName && !(row.client || '').toLowerCase().includes(clientName.toLowerCase())) return false
        if (initial && !(row.initial || '').toLowerCase().includes(initial.toLowerCase())) return false
        if (businessType && row.client_type !== businessType) return false
        if (serviceTypes.length) {
          const names = (row.category || []).map(c => c.name)
          if (!serviceTypes.some(s => names.includes(s))) return false
        }
        if (yearEnd && row.year_end !== parseInt(yearEnd)) return false

        for (const col of GB_COL_DEFS) {
          const fv = cf[col.key]
          if (!fv) continue
          const val = this.getCatColValue(row, col)
          if (fv === 'has' && (val === null || val === undefined)) return false
          if (fv === 'empty' && (val !== null && val !== undefined)) return false
          if (['pending', 'review', 'completed'].includes(fv) && (val ?? '') !== fv) return false
        }
        return true
      })
    },

    eligibleCount () {
      return this.filteredRows.filter(r => r.status === 'pending' && this.buildToList(r).length > 0).length
    },
    noRecipientCount () {
      return this.filteredRows.filter(r => this.buildToList(r).length === 0).length
    },
    sentCount () { return this.tableRows.filter(r => r.status === 'sent').length },
    skippedCount () { return this.tableRows.filter(r => r.status === 'skipped').length },
    failedCount () { return this.tableRows.filter(r => r.status === 'failed').length },

    hasActiveFilters () {
      const { f, catFilters } = this
      return !!(f.clientName || f.initial || f.businessType || f.serviceTypes.length || f.yearEnd ||
        Object.values(catFilters).some(v => v))
    }
  },

  watch: {
    'blasting.active' (val) { val ? lockBodyScroll() : unlockBodyScroll() },
    'summary.show' (val) { val ? lockBodyScroll() : unlockBodyScroll() }
  },

  mounted () {
    this.startSessionTimer()
    this.fetchData()
    document.addEventListener('click', this.onOutsideClick)
    this._beforeUnload = e => {
      if (this.blasting.active) { e.preventDefault(); e.returnValue = '' }
    }
    window.addEventListener('beforeunload', this._beforeUnload)
  },

  beforeDestroy () {
    document.removeEventListener('click', this.onOutsideClick)
    window.removeEventListener('beforeunload', this._beforeUnload)
    if (this.blasting.active || this.summary.show) unlockBodyScroll()
    if (this.attachment.token) this.deleteAttachment(this.attachment.token)
  },

  methods: {
    async fetchData () {
      this.loading = true
      try {
        const data = await this.adminFetch('/api/admin/tasks/general-email-blast-clients.php')
        if (data === null) { this.pendingAdminRetry = () => this.fetchData(); return }
        if (data && data.success) this.buildRows(data.data || [])
      } catch (err) {
        console.error('Failed to load clients:', err)
      } finally {
        this.loading = false
      }
    },

    buildRows (clients) {
      this.tableRows = clients.map(c => {
        const picRecipients = []
        const toRecipients = []
        const ccRecipients = []
        const bccRecipients = []
        const directorRecipients = []

        for (const card of (c.contacts || [])) {
          const email = (card.email || '').trim()
          const name = (card.name || '').trim()
          const roles = card.roles || []
          if (!email) continue
          if (roles.includes('pic')) picRecipients.push({ email, name })
          if (roles.includes('director')) directorRecipients.push({ email, name })
          if (roles.includes('to')) toRecipients.push({ email, name, enabled: true })
          if (roles.includes('cc')) ccRecipients.push({ email, name, enabled: true })
          if (roles.includes('bcc')) bccRecipients.push({ email, name, enabled: true })
        }

        return {
          id: c.id,
          client: c.client,
          initial: c.initial,
          clientStatus: c.client_status || 'active',
          client_type: c.client_type,
          year_end: c.year_end,
          category: c.category || [],
          picRecipients,
          toRecipients,
          ccRecipients,
          bccRecipients,
          directorRecipients,
          status: 'pending',
          error: null,
          failedRecipients: []
        }
      })
    },

    buildToList (row) {
      const to = []
      const seen = new Set()
      const add = r => { if (!seen.has(r.email)) { seen.add(r.email); to.push({ email: r.email, name: r.name }) } }
      if (this.emailGroupFilters.pic) row.picRecipients.forEach(add)
      if (this.emailGroupFilters.director) row.directorRecipients.forEach(add)
      if (this.emailGroupFilters.to) row.toRecipients.filter(r => r.enabled).forEach(add)
      return to
    },
    buildCcList (row) {
      if (!this.emailGroupFilters.cc) return []
      return row.ccRecipients.filter(r => r.enabled).map(r => ({ email: r.email, name: r.name }))
    },
    buildBccList (row) {
      if (!this.emailGroupFilters.bcc) return []
      return row.bccRecipients.filter(r => r.enabled).map(r => ({ email: r.email, name: r.name }))
    },

    async runBlast () {
      if (this.blasting.active) return
      if (!this.composer.subject.trim()) {
        this.$refs.subjectInput && this.$refs.subjectInput.focus()
        alert('Please enter an email subject before blasting.')
        return
      }
      if (!this.composer.messageHtml.trim()) {
        this.$refs.editor && this.$refs.editor.focus()
        alert('Please enter a message body before blasting.')
        return
      }
      this.blasting.active = true

      const eligible = this.filteredRows.filter(r => r.status === 'pending' && this.buildToList(r).length > 0)

      for (let i = 0; i < eligible.length; i++) {
        const row = eligible[i]
        this.$set(row, 'status', 'sending')
        try {
          const data = await this.adminFetch('/api/admin/tasks/send-general-email-blast.php', {
            method: 'POST',
            body: JSON.stringify({
              client_id: row.id,
              subject: this.composer.subject,
              message_html: this.composer.messageHtml,
              to_emails: this.buildToList(row),
              cc_emails: this.buildCcList(row),
              bcc_emails: this.buildBccList(row),
              attachment_token: this.attachment.token || null
            })
          })
          if (data === null) {
            this.$set(row, 'status', 'failed')
            this.$set(row, 'error', 'Session expired')
            break
          }
          const status = data.status || (data.success ? 'sent' : 'failed')
          this.$set(row, 'status', status)
          if (data.failed_recipients && data.failed_recipients.length) this.$set(row, 'failedRecipients', data.failed_recipients)
          if (data.reason) this.$set(row, 'error', data.reason)
          if (data.error) this.$set(row, 'error', data.error)
        } catch (err) {
          this.$set(row, 'status', 'failed')
          this.$set(row, 'error', err.message)
        }
        if (i < eligible.length - 1) await new Promise(resolve => setTimeout(resolve, 2000 + Math.random() * 1000))
      }

      // Delete temp attachment from server now that all sends are done
      if (this.attachment.token) {
        const tok = this.attachment.token
        this.attachment = { token: null, name: '', size: 0, uploading: false }
        if (this.$refs.attachInput) this.$refs.attachInput.value = ''
        this.deleteAttachment(tok)
      }

      this.blasting.active = false
      this.blasting.done = true
      this.openSummary()
    },

    openSummary () {
      const rows = this.tableRows
      this.summary = {
        show: true,
        sent: rows.filter(r => r.status === 'sent').length,
        partial: rows.filter(r => r.status === 'partial').length,
        skipped: rows.filter(r => r.status === 'skipped').length,
        failed: rows.filter(r => r.status === 'failed').length,
        errors: rows.filter(r => r.status === 'failed').map(r => ({ client: r.client, error: r.error }))
      }
    },

    resetAndCloseSummary () {
      this.summary.show = false
      this.blasting.done = false
      this.tableRows.forEach(r => { this.$set(r, 'status', 'pending') })
    },

    async uploadAttachment (file) {
      const base = process.env.VUE_APP_API_BASE || 'https://tyng-ca.com.my'
      const token = localStorage.getItem('adminToken')
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(base + '/api/admin/tasks/upload-blast-attachment.php', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token },
        body: formData
      })
      return res.json()
    },

    deleteAttachment (token) {
      this.adminFetch('/api/admin/tasks/delete-blast-attachment.php', {
        method: 'POST',
        body: JSON.stringify({ token })
      }).catch(() => {})
    },

    async onAttachmentChange (e) {
      const file = e.target.files[0]
      if (!file) return

      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'image/jpeg',
        'image/png',
        'text/csv'
      ]

      this.attachError = ''

      if (!allowedTypes.includes(file.type)) {
        this.attachError = 'File type not allowed. Use PDF, Word, Excel, image, or CSV.'
        e.target.value = ''
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.attachError = 'File too large (' + this.formatFileSize(file.size) + '). Maximum is 5 MB.'
        e.target.value = ''
        return
      }

      // Remove any previously uploaded attachment before uploading the new one
      if (this.attachment.token) this.deleteAttachment(this.attachment.token)

      this.attachment = { token: null, name: '', size: 0, uploading: true }
      try {
        const data = await this.uploadAttachment(file)
        if (data && data.success) {
          this.attachment = { token: data.token, name: data.original_name, size: data.size, uploading: false }
        } else {
          this.attachError = (data && data.message) || 'Upload failed.'
          this.attachment = { token: null, name: '', size: 0, uploading: false }
        }
      } catch (err) {
        this.attachError = 'Upload failed.'
        this.attachment = { token: null, name: '', size: 0, uploading: false }
      }
    },

    removeAttachment () {
      if (this.attachment.token) this.deleteAttachment(this.attachment.token)
      this.attachment = { token: null, name: '', size: 0, uploading: false }
      this.attachError = ''
      if (this.$refs.attachInput) this.$refs.attachInput.value = ''
    },

    formatFileSize (bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },

    execCmd (cmd) {
      if (this.$refs.editor) this.$refs.editor.focus()
      document.execCommand(cmd, false, null)
      this.composer.messageHtml = this.$refs.editor ? this.$refs.editor.innerHTML : ''
    },

    onEditorInput () {
      this.composer.messageHtml = this.$refs.editor ? this.$refs.editor.innerHTML : ''
    },

    toggleGroup (key) {
      const idx = this.collapsedGroups.indexOf(key)
      idx === -1 ? this.collapsedGroups.push(key) : this.collapsedGroups.splice(idx, 1)
    },

    clearAllFilters () {
      this.f = { clientName: '', initial: '', businessType: '', serviceTypes: [], yearEnd: '' }
      GB_COL_DEFS.forEach(d => { this.catFilters[d.key] = '' })
    },

    onOutsideClick (e) {
      if (this.$refs.svcDropdown && !this.$refs.svcDropdown.contains(e.target)) this.showSvcMenu = false
    },

    getCatColValue (row, colDef) {
      const cat = (row.category || []).find(c => c.name === colDef.cat)
      if (!cat) return null
      const [p0, p1] = colDef.path
      if (!p1) {
        const item = (cat.sub_category || []).find(s => s.name === p0)
        if (!item) return null
        return item.date !== undefined ? item.date : (item.status ?? null)
      }
      const sub = (cat.sub_category || []).find(s => s.name === p0)
      if (!sub) return null
      const task = (sub.tasks || []).find(t => t.name === p1)
      if (!task) return null
      return task.date !== undefined ? task.date : (task.status ?? null)
    },

    formatCatColValue (val, type) {
      if (val === null || val === undefined) return '—'
      if (type === 'status') {
        const map = { pending: 'Pending', review: 'Review', completed: 'Done', na: 'N/A' }
        return map[String(val).toLowerCase()] || val
      }
      if (val && typeof val === 'object') {
        if (val.day && val.month) return `${val.day} ${MONTHS[val.month - 1]}`
        if (val.month) return MONTHS[val.month - 1]
        if (val.day) return `Day ${val.day}`
      }
      return '—'
    },

    blastBadgeClass (row) {
      if (row.status === 'no-recipient') return 'tp-notify-badge--skipped'
      return 'tp-notify-badge--' + row.status
    },

    blastStatusLabel (row) {
      if (row.status === 'partial') {
        const total = this.buildToList(row).length + this.buildCcList(row).length + this.buildBccList(row).length
        const failed = (row.failedRecipients || []).length
        return `Partial — ${total - failed} sent, ${failed} failed`
      }
      const map = { pending: 'Pending', sending: 'Sending…', sent: 'Sent', failed: 'Failed', skipped: row.error || 'Skipped', 'no-recipient': 'No Recipient' }
      return map[row.status] || row.status
    },

    formatClientType (val) {
      const map = { sdn_bhd: 'Sdn Bhd', llp: 'LLP', sole_prop: 'Sole Prop', partnership: 'Partnership', salaried_person: 'Salaried' }
      return map[val] || val || '—'
    },

    formatYearEnd (month) {
      if (!month) return '—'
      return MONTHS[month - 1] || '—'
    }
  }
}
</script>
